import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import { EventStaffAssignment, EventStaffing } from '../types';

type Props = {
  eventOrderId: number;
  eventDate: string;
  canEdit: boolean;
  onLaborCostChanged: (laborCost: number) => void;
};

type FormState = {
  appUserId: string;
  role: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string;
  actualEnd: string;
  hourlyCost: string;
  notes: string;
};

const emptyForm = (eventDate: string): FormState => {
  const date = eventDate.slice(0, 10);
  return {
    appUserId: '',
    role: '',
    plannedStart: `${date}T10:00`,
    plannedEnd: `${date}T18:00`,
    actualStart: '',
    actualEnd: '',
    hourlyCost: '0',
    notes: '',
  };
};

const toInputDateTime = (value: string | null): string => value ? value.slice(0, 16) : '';
const formatHours = (value: number | null): string => value == null ? '—' : `${value.toFixed(2)} год`;
const formatMoney = (value: number | null): string => value == null ? '—' : `${value.toFixed(2)} грн`;

const EventStaffingPanel: React.FC<Props> = ({ eventOrderId, eventDate, canEdit, onLaborCostChanged }) => {
  const [staffing, setStaffing] = useState<EventStaffing | null>(null);
  const [form, setForm] = useState<FormState>(() => emptyForm(eventDate));
  const [editing, setEditing] = useState<EventStaffAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const response = await api.get<EventStaffing>(`/eventorders/${eventOrderId}/staffing`);
      setStaffing(response.data);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Не вдалося завантажити план персоналу.');
    } finally {
      setLoading(false);
    }
  }, [eventOrderId]);

  useEffect(() => {
    setEditing(null);
    setForm(emptyForm(eventDate));
    load();
  }, [eventDate, load]);

  const selectedEmployee = useMemo(
    () => staffing?.employees.find(employee => employee.id === form.appUserId) ?? null,
    [form.appUserId, staffing?.employees]
  );

  const updateEmployee = (appUserId: string) => {
    const employee = staffing?.employees.find(item => item.id === appUserId);
    setForm(current => ({
      ...current,
      appUserId,
      role: current.role || employee?.position || '',
      hourlyCost: editing ? current.hourlyCost : String(employee?.defaultHourlyCost ?? 0),
    }));
  };

  const beginEdit = (assignment: EventStaffAssignment) => {
    setEditing(assignment);
    setForm({
      appUserId: assignment.appUserId ?? '',
      role: assignment.role,
      plannedStart: toInputDateTime(assignment.plannedStart),
      plannedEnd: toInputDateTime(assignment.plannedEnd),
      actualStart: toInputDateTime(assignment.actualStart),
      actualEnd: toInputDateTime(assignment.actualEnd),
      hourlyCost: String(assignment.hourlyCost),
      notes: assignment.notes,
    });
  };

  const resetForm = () => {
    setEditing(null);
    setForm(emptyForm(eventDate));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    const payload = {
      appUserId: form.appUserId,
      role: form.role,
      plannedStart: form.plannedStart,
      plannedEnd: form.plannedEnd,
      actualStart: form.actualStart || null,
      actualEnd: form.actualEnd || null,
      hourlyCost: Number(form.hourlyCost),
      notes: form.notes,
    };
    try {
      const response = editing
        ? await api.put<EventStaffing>(
            `/eventorders/${eventOrderId}/staffing/${editing.id}`,
            payload,
            { headers: { 'If-Match': `"${editing.version}"` } }
          )
        : await api.post<EventStaffing>(`/eventorders/${eventOrderId}/staffing`, payload);
      setStaffing(response.data);
      onLaborCostChanged(response.data.summary.laborCostSnapshot);
      resetForm();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Не вдалося зберегти призначення.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (assignment: EventStaffAssignment) => {
    if (!window.confirm(`Видалити ${assignment.employeeName} з плану події?`)) return;
    setSaving(true);
    setMessage('');
    try {
      const response = await api.delete<EventStaffing>(
        `/eventorders/${eventOrderId}/staffing/${assignment.id}`,
        { headers: { 'If-Match': `"${assignment.version}"` } }
      );
      setStaffing(response.data);
      onLaborCostChanged(response.data.summary.laborCostSnapshot);
      if (editing?.id === assignment.id) resetForm();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Не вдалося видалити призначення.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="event-order-view-section event-themed-panel event-staffing-panel">
      <div className="event-order-view-section-title" style={{ fontWeight: 700, fontSize: 14, borderLeft: '4px solid #27ae60', paddingLeft: 10, marginBottom: 10 }}>
        Команда події: план / факт
      </div>
      {loading && <div style={{ color: 'var(--crm-paper-muted)', fontSize: 13 }}>Завантаження персоналу…</div>}
      {message && <div style={{ color: 'var(--ev-danger)', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{message}</div>}
      {staffing && (
        <>
          <div className="event-themed-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))', gap: 8, marginBottom: 10 }}>
            <div><small>Працівників</small><div><strong>{staffing.summary.assignmentCount}</strong></div></div>
            <div><small>Планові години</small><div><strong>{formatHours(staffing.summary.plannedHours)}</strong></div></div>
            <div><small>Фактичні години</small><div><strong>{formatHours(staffing.summary.actualHours)}</strong></div></div>
            <div><small>Планова вартість</small><div><strong>{formatMoney(staffing.summary.plannedCost)}</strong></div></div>
            <div><small>Знімок витрат</small><div><strong>{formatMoney(staffing.summary.laborCostSnapshot)}</strong></div></div>
          </div>
          <div className="event-order-view-table-wrap">
            <table className="event-order-view-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 6 }}>Працівник / роль</th>
                  <th style={{ textAlign: 'left', padding: 6 }}>План</th>
                  <th style={{ textAlign: 'left', padding: 6 }}>Факт</th>
                  <th style={{ textAlign: 'right', padding: 6 }}>Ставка</th>
                  <th style={{ textAlign: 'right', padding: 6 }}>Витрати</th>
                  {canEdit && <th style={{ padding: 6 }} />}
                </tr>
              </thead>
              <tbody>
                {staffing.assignments.map(assignment => (
                  <tr key={assignment.id}>
                    <td style={{ padding: 6 }}>
                      <strong>{assignment.employeeName}</strong>
                      <div style={{ color: 'var(--crm-paper-muted)' }}>{assignment.role}{assignment.position ? ` · ${assignment.position}` : ''}</div>
                    </td>
                    <td style={{ padding: 6 }}>
                      {new Date(assignment.plannedStart).toLocaleString('uk-UA')}<br />
                      {formatHours(assignment.plannedHours)}
                    </td>
                    <td style={{ padding: 6 }}>
                      {assignment.actualStart ? new Date(assignment.actualStart).toLocaleString('uk-UA') : 'Не внесено'}<br />
                      {formatHours(assignment.actualHours)}
                    </td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{formatMoney(assignment.hourlyCost)}</td>
                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>
                      {formatMoney(assignment.effectiveCost)}
                      <div style={{ color: assignment.actualCost == null ? 'var(--ev-warning)' : 'var(--ev-success)', fontSize: 11 }}>
                        {assignment.actualCost == null ? 'за планом' : 'за фактом'}
                      </div>
                    </td>
                    {canEdit && (
                      <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                        <button type="button" onClick={() => beginEdit(assignment)}>Редагувати</button>{' '}
                        <button type="button" onClick={() => remove(assignment)} disabled={saving}>Видалити</button>
                      </td>
                    )}
                  </tr>
                ))}
                {staffing.assignments.length === 0 && (
                  <tr><td colSpan={canEdit ? 6 : 5} style={{ padding: 10, textAlign: 'center', color: 'var(--crm-paper-muted)' }}>Команду ще не заплановано.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <form onSubmit={save} style={{ marginTop: 12, borderTop: '1px solid var(--crm-paper-border)', paddingTop: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{editing ? 'Редагування призначення' : 'Додати працівника'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                <label>Працівник
                  <select required value={form.appUserId} onChange={event => updateEmployee(event.target.value)} style={{ width: '100%' }}>
                    <option value="">Оберіть…</option>
                    {staffing.employees.map(employee => <option key={employee.id} value={employee.id}>{employee.fullName} · {employee.position || 'Працівник'}</option>)}
                  </select>
                </label>
                <label>Роль на події<input required maxLength={100} value={form.role} onChange={event => setForm({ ...form, role: event.target.value })} style={{ width: '100%' }} /></label>
                <label>План: початок<input required type="datetime-local" value={form.plannedStart} onChange={event => setForm({ ...form, plannedStart: event.target.value })} style={{ width: '100%' }} /></label>
                <label>План: завершення<input required type="datetime-local" value={form.plannedEnd} onChange={event => setForm({ ...form, plannedEnd: event.target.value })} style={{ width: '100%' }} /></label>
                <label>Факт: початок<input type="datetime-local" value={form.actualStart} onChange={event => setForm({ ...form, actualStart: event.target.value })} style={{ width: '100%' }} /></label>
                <label>Факт: завершення<input type="datetime-local" value={form.actualEnd} onChange={event => setForm({ ...form, actualEnd: event.target.value })} style={{ width: '100%' }} /></label>
                <label>Собівартість години<input required type="number" min="0" step="0.01" value={form.hourlyCost} onChange={event => setForm({ ...form, hourlyCost: event.target.value })} style={{ width: '100%' }} /></label>
                <label>Примітка<input maxLength={1000} value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} style={{ width: '100%' }} /></label>
              </div>
              {selectedEmployee && selectedEmployee.defaultHourlyCost === 0 && (
                <div style={{ color: 'var(--ev-warning)', fontSize: 12, marginTop: 6 }}>Для працівника не задано базову погодинну ставку — перевірте значення вручну.</div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
                <button type="submit" disabled={saving}>{saving ? 'Збереження…' : editing ? 'Зберегти зміни' : 'Додати до команди'}</button>
                {editing && <button type="button" onClick={resetForm} disabled={saving}>Скасувати</button>}
              </div>
            </form>
          )}
          <div style={{ marginTop: 8, color: 'var(--crm-paper-muted)', fontSize: 11 }}>
            Знімок витрат використовує фактичні години для завершених змін, а для решти — планові. Зміни команди скидають підтвердження витрат події.
          </div>
        </>
      )}
    </div>
  );
};

export default EventStaffingPanel;
